import { useMutation, useQueryClient } from '@tanstack/react-query';
import { maxUint256 } from 'viem';
import { useWalletClient } from 'wagmi';
import { ERC20_ABI } from '../config/oft';
import { STARGATE_USDC_POOLS } from '../config/stargate';

/**
 * Approves USDC for the StargateV2 pool contract.
 * Approves maxUint256 to avoid re-approval for future sends.
 *
 * @param sourceChainId - EVM chain ID of the source chain
 */
export function useStargateApprove(sourceChainId: number | undefined) {
  const { data: walletClient } = useWalletClient({
    chainId: sourceChainId,
  });
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!sourceChainId) {
        throw new Error('sourceChainId is required');
      }
      if (!walletClient) {
        throw new Error('Wallet not connected');
      }

      const poolConfig = STARGATE_USDC_POOLS[sourceChainId];
      if (!poolConfig) {
        throw new Error(`No Stargate USDC pool for chainId ${sourceChainId}`);
      }

      const txHash = await walletClient.writeContract({
        address: poolConfig.usdcAddress,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [poolConfig.poolAddress, maxUint256],
        chain: walletClient.chain,
        account: walletClient.account,
      });

      return txHash;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['stargateAllowance', sourceChainId],
      });
    },
  });
}
